import React, { useState } from 'react';
import { Button } from '../common/ui/Button';
import { Input } from '../common/ui/Input';
import { supabase } from '../../lib/supabase';
import { captureCustomEvent } from '../../utils/posthogUtils';

interface VerificationCodeInputProps {
  email: string;
  onResend: () => void;
  loading: boolean;
  resendDisabled: boolean;
  resendTimer: number;
  submitButtonText?: string;
  infoTextLines?: string[];
  otpType: 'signup' | 'recovery' | 'email';
  onVerificationSuccess: () => void;
  onVerificationError: (errorMessage: string) => void;
  captchaToken?: string;
}

export const VerificationCodeInput: React.FC<VerificationCodeInputProps> = ({
  email,
  onResend,
  loading,
  resendDisabled,
  resendTimer,
  submitButtonText = 'Continue',
  infoTextLines = [
    'We have sent an email with',
    'a 6 digit code number to',
  ],
  otpType,
  onVerificationSuccess,
  onVerificationError,
  captchaToken,
}) => {
  const [internalCode, setInternalCode] = useState<string[]>(['', '', '', '', '', '']);

  const isSubmitDisabled = internalCode.some(digit => !digit);

  const handleCodeChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCode = [...internalCode];
      newCode[index] = value;
      setInternalCode(newCode);

      if (value && index < 5 && value.length === 1) {
        const nextInput = document.getElementById(`code-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !internalCode[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      setInternalCode(pastedData.split(''));
      const lastInput = document.getElementById('code-5');
      if (lastInput) {
        lastInput.focus();
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitDisabled || loading) return;

    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: internalCode.join(''),
        type: otpType,
        options: { captchaToken },
      });

      if (error) {
        throw error;
      }
      captureCustomEvent('otp_verification_success', { eventProperties: { otpType, email } });
      onVerificationSuccess();
    } catch (err) {
      captureCustomEvent('otp_verification_failure', { eventProperties: { otpType, email, error: err instanceof Error ? err.message : 'Unknown error' } });
      onVerificationError(err instanceof Error ? err.message : 'Failed to verify code.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-blue-50 p-4 rounded-lg text-sm text-center">
        {infoTextLines.map((line, idx) => (
          <p key={idx}>{line}</p>
        ))}
        <p className="font-bold">{email}</p>
        <p className="mt-2">Do not share this code with anyone!</p>
        <p className="text-xs mt-2">
          You'll receive this email within 5 minutes. Be sure to check your spam folder, too.
        </p>
      </div>

      <div
        className="flex justify-center gap-2"
        onPaste={handlePaste}
      >
        {internalCode.map((digit, index) => (
          <Input
            key={index}
            id={`code-${index}`}
            type="text"
            maxLength={1}
            value={digit}
            onChange={(e) => handleCodeChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className="!w-10 !h-12 text-center !p-0"
            autoComplete="one-time-code"
            inputMode="numeric"
          />
        ))}
      </div>

      <Button
        type="submit"
        disabled={loading || isSubmitDisabled}
        loading={loading}
        className="w-full"
      >
        {submitButtonText}
      </Button>

      <div className="text-center">
        <p className="text-sm text-gray-600">
          Didn't receive an email?{' '}
          <button
            type="button"
            onClick={onResend}
            disabled={resendDisabled}
            className={`text-blue-600 hover:text-blue-700 ${resendDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {resendDisabled
              ? `Resend available in ${Math.floor(resendTimer / 60)}:${(resendTimer % 60).toString().padStart(2, '0')}`
              : 'Send again'
            }
          </button>
        </p>
      </div>
    </form>
  );
}; 